import type { UserCreateSchema } from '@repo/schemas/user/userCreate';
import type { UserEditSchemaEditPayload } from '@repo/schemas/user/userEdit';
import type { GroupPrivate } from '@repo/types/groups';
import type { PaginationWithName } from '@repo/types/pagination';
import type { UserPrivate, UserPublic } from '@repo/types/users';
import type { AxiosResponse } from 'axios';

import { axiosInstance } from '#api-client';

export class UserService {
  static async getManagedUsers(): Promise<AxiosResponse<Array<UserPrivate>>> {
    return await axiosInstance.get('/api/user/managed');
  }

  static async getUsers(params: PaginationWithName): Promise<AxiosResponse<Array<UserPublic>>> {
    return await axiosInstance.get('/api/user/all', {
      params: params,
    });
  }

  static async getUser(id?: number): Promise<AxiosResponse<UserPrivate>> {
    return await axiosInstance.get(`/api/user/${id}`);
  }

  static async getUserGroup(id?: number): Promise<AxiosResponse<Array<GroupPrivate>>> {
    return await axiosInstance.get(`/api/user/${id}/groups`);
  }

  static async createUser(data: UserCreateSchema): Promise<AxiosResponse<UserPrivate>> {
    return await axiosInstance.post('/api/user', data);
  }

  static async editUser(data: UserEditSchemaEditPayload): Promise<AxiosResponse<UserPrivate>> {
    const { id, ...rest } = data;
    return await axiosInstance.put(`/api/user/${id}`, rest);
  }

  static async deleteUser(id: number): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete(`/api/user/${id}`);
  }

  static async resendInvite(id: number): Promise<AxiosResponse<void>> {
    return await axiosInstance.post(`/api/user/${id}/invite`);
  }

  static async deleteUserAvatar(data: { authToken?: string }): Promise<AxiosResponse<string>> {
    return await axiosInstance.delete('/api/user/avatar', { data: data });
  }

  static async upsertUserAvatar(data: UserAvatarUploadData): Promise<AxiosResponse<{ avatarURL: string }>> {
    const { b64, authToken } = data;
    const formData = new FormData();
    formData.append('avatar', b64);
    if (authToken) {
      formData.append('authToken', authToken);
    }
    return await axiosInstance.post('/api/user/avatar', formData);
  }
}

export interface UserAvatarUploadData {
  b64: Blob;
  authToken?: string;
}
